import React from 'react';
import { PRODUCTS, INDUSTRIES, MARQUEE, WHY, COMPARE, STEPS } from './data.js';
import { Link, Shot, accentVars, scrollToId } from './ui.jsx';
import { Hero } from './hero.jsx';

export function HomePage() {
  return (
    <>
      <Hero />
      <Marquee />
      <WhySection />
      <ProductsSection />
      <SolutionsSection />
      <RoiSection />
      <StepsSection />
      <section className="wrap sec grid g2 company-teaser">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="eyebrow">COMPANY</div>
          <h2>Small. Senior. Hands on every detail.</h2>
          <p className="muted" style={{ fontSize: 16, lineHeight: 1.5, maxWidth: 520 }}>A Toronto team across computer vision, ML, edge and industrial hardware. Every customer works directly with an engineer.</p>
        </div>
        <div className="cta-wrap">
          <Link to="/about" className="btn-dark">Meet the company and team →</Link>
        </div>
      </section>
    </>
  );
}

function Marquee() {
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-track">
        {MARQUEE.concat(MARQUEE).map((m, i) => <div key={i}><div>{m}</div><i /></div>)}
      </div>
    </div>
  );
}

function WhySection() {
  return (
    <section className="wrap sec-lg" style={{ paddingBottom: 64 }}>
      <div className="eyebrow">WHY PLANTS SWITCH</div>
      <h2 className="h2" style={{ marginBottom: 40 }}>Lower cost. Lower risk. Nothing to babysit.</h2>
      <div className="grid g3 why">
        {WHY.map(w => (
          <div className="why-card" key={w.n}>
            <div className="n">{w.n}</div>
            <div className="t">{w.t}</div>
            <div className="d">{w.d}</div>
          </div>
        ))}
      </div>
      <div className="cmp">
        <div className="cmp-row cmp-head">
          <div />
          <div className="cmp-head-cell" style={{ color: 'var(--reject-ink)' }}><div className="mark lg x">✕</div><div>THE USUAL WAY</div></div>
          <div />
          <div className="cmp-head-cell" style={{ color: 'var(--ink)', fontWeight: 500 }}><div className="mark lg ok">✓</div><div>WITH VIAI</div></div>
        </div>
        {COMPARE.map(c => (
          <div className="cmp-row" key={c.k}>
            <div className="cmp-k">{c.k}</div>
            <div className="cmp-old"><div className="mark x" aria-hidden="true">✕</div><div><span className="sr-only">The usual way: </span>{c.old}</div></div>
            <div className="cmp-arrow" aria-hidden="true">→</div>
            <div className="cmp-new"><div className="mark ok" aria-hidden="true">✓</div><div><span className="sr-only">With VIAI: </span>{c.neu}</div></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductsSection() {
  const big = PRODUCTS.slice(0, 2);
  const small = PRODUCTS.slice(2);
  return (
    <section id="home-products" className="band-2 home-products">
      <div className="wrap sec">
        <div className="sec-head">
          <div>
            <div className="eyebrow">PRODUCTS</div>
            <h2 className="h2">Five products. One inspection system.</h2>
          </div>
          <div className="side">Sold alone or as one suite. One model format, one scoring scale, one recipe format.</div>
        </div>
        <div className="grid g2">
          {big.map((p, i) => (
            <Link to={'/product/' + p.key} className="prod-big" key={p.key} style={accentVars(p.key)}>
              <div className="top">
                <div className="k">{`0${i + 1} · ${p.kind}`}</div>
                <div className="n">{p.name}</div>
                <div className="d">{p.short}</div>
              </div>
              <div className="shot"><Shot src={p.homeSrc} alt={p.shot1} /></div>
              <div className="more">{`View ${p.name} →`}</div>
            </Link>
          ))}
        </div>
        <div className="grid g3" style={{ marginTop: 20 }}>
          {small.map((p, i) => (
            <Link to={'/product/' + p.key} className="prod-small" key={p.key} style={accentVars(p.key)}>
              <div className="k">{`0${i + 3} · ${p.kind}`}</div>
              <div className="n">{p.name}</div>
              <div className="d">{p.short}</div>
              <div className="link-arrow" style={{ marginTop: 8 }}>View product →</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function SolutionsSection() {
  return (
    <section id="solutions" className="wrap sec-lg">
      <div className="eyebrow">SOLUTIONS</div>
      <h2 className="h2">If a camera can see it, VIAI can grade it.</h2>
      <div className="grid g4 ind-grid">
        {INDUSTRIES.map((d, i) => (
          <Link to={'/solutions/' + d.key} className="ind-card" key={d.key}>
            <div className="num">{`0${i + 1}`}</div>
            <div className="n">{d.name}</div>
            <div className="d">{d.teaser}</div>
            <div className="link-arrow">See the solution →</div>
          </Link>
        ))}
        <a href="#contact" className="ind-card dashed" onClick={(e) => { e.preventDefault(); scrollToId('contact'); }}>
          <div className="num">+</div>
          <div className="n">Your industry</div>
          <div className="d">Send us 50 sample images. We show you a working detector.</div>
          <div className="link-arrow">Start here →</div>
        </a>
      </div>
    </section>
  );
}

const ROI_GROUPS = [
  { t: 'Defects & cost', items: [['lines', 'Production lines', 'lines'], ['dps', 'Defects per shift, per line', 'defects'], ['cpe', 'Cost per escaped defect', 'USD']] },
  { t: 'Inspectors', items: [['rate', 'Hourly rate', '$/hr'], ['insp', 'Inspectors per shift', 'people'], ['cost', 'Your quoted system cost per line', 'USD', 'optional']] },
  { t: 'Schedule', items: [['hours', 'Hours per shift', 'hr'], ['shifts', 'Shifts per day', 'shifts'], ['days', 'Operating days per year', 'days']] },
];
const CATCH_OPTS = [{ l: 'Conservative', v: 0.85 }, { l: 'Typical', v: 0.92 }, { l: 'Best case', v: 0.96 }];
const usd = (v) => '$' + Math.round(v).toLocaleString('en-US');
const int = (v) => Math.round(v).toLocaleString('en-US');

// Assumes 30% of defects escape manual inspection (so VIAI's gain is its
// catch rate above 70%) and that VIAI frees half of inspector time.
function roiModel(r, cr) {
  const n = (k) => Math.max(0, parseFloat(r[k]) || 0);
  const total = n('lines') * n('dps') * n('shifts') * n('days');
  const esc = Math.max(0, cr - 0.7) * total * n('cpe');
  const insp = 0.5 * n('rate') * n('insp') * n('hours') * n('shifts') * n('days') * n('lines');
  const annual = esc + insp;
  const cost = n('cost') * n('lines');
  let payback = 'Enter quote', ret = 'Enter quote';
  if (cost > 0 && annual > 0) {
    const m = cost / (annual / 12);
    payback = m < 1 ? '< 1 month' : m.toFixed(1) + ' months';
    ret = Math.round((3 * annual - cost) / cost * 100).toLocaleString('en-US') + '%';
  }
  return { annual: usd(annual), esc: usd(esc), insp: usd(insp), payback, ret, caught: int(total * cr), total: int(total) };
}

function RoiSection() {
  const [roi, setRoi] = React.useState({ lines: 1, dps: 10, cpe: 85, rate: 28, insp: 2, hours: 8, shifts: 2, days: 300, cost: '' });
  const [catchRate, setCatchRate] = React.useState(0.92);
  const out = roiModel(roi, catchRate);
  return (
    <section id="roi" className="band-2">
      <div className="wrap sec">
        <div className="eyebrow">RETURN ON INVESTMENT</div>
        <h2 className="h2" style={{ marginBottom: 12 }}>What does this save you, in dollars?</h2>
        <p className="lede" style={{ marginBottom: 40 }}>Plug in your line. Savings are based on your numbers, not ours.</p>
        <div className="grid roi">
          <div className="roi-form">
            {ROI_GROUPS.map(g => (
              <div className="roi-group" key={g.t}>
                <div className="t">{g.t}</div>
                <div className="roi-fields">
                  {g.items.map(([k, label, unit, ph]) => (
                    <label className="field" key={k}>
                      <div className="lbl">{label}</div>
                      <div className="box">
                        <input type="number" min="0" inputMode="decimal" value={roi[k]} placeholder={ph || ''}
                               onChange={(e) => { const v = e.target.value; setRoi(s => ({ ...s, [k]: v })); }} />
                        <div className="unit">{unit}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="roi-group">
              <div className="t" id="catch-label">Estimated VIAI catch rate</div>
              <div className="seg" role="group" aria-labelledby="catch-label">
                {CATCH_OPTS.map(o => (
                  <button type="button" key={o.l} aria-pressed={o.v === catchRate} onClick={() => setCatchRate(o.v)}>
                    <span className="l">{o.l}</span><span className="v">{`${Math.round(o.v * 100)}%`}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="roi-out" aria-live="polite">
            <div className="eyebrow" style={{ color: 'var(--mutedDk)', fontSize: 12, letterSpacing: '.08em' }}>YOUR YEAR WITH VIAI</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 14, color: 'var(--sub)' }}>Estimated annual savings</div>
              <div className="big">{out.annual}</div>
            </div>
            <div className="roi-chips">
              <div className="roi-chip g"><b>{out.esc}</b> fewer escapes</div>
              <div className="roi-chip v"><b>{out.insp}</b> inspector time</div>
            </div>
            <div className="roi-tiles">
              <div className="roi-tile"><div className="k">PAYBACK</div><div className="v">{out.payback}</div></div>
              <div className="roi-tile"><div className="k">3-YEAR RETURN</div><div className="v">{out.ret}</div></div>
              <div className="roi-tile"><div className="k">DEFECTS CAUGHT / YR</div><div className="v">{out.caught}</div></div>
              <div className="roi-tile"><div className="k">TOTAL DEFECTS / YR</div><div className="v">{out.total}</div></div>
            </div>
            <a href="#contact" className="roi-cta" onClick={(e) => { e.preventDefault(); scrollToId('contact'); }}>Get a return analysis on your real numbers →</a>
            <div className="roi-note">Assumes 30% of defects escape manual inspection and VIAI frees about half of inspector time. Payback and return appear when you enter your quoted system cost. Your pilot produces real numbers.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <section className="band-dk">
      <div className="wrap sec-lg">
        <div className="eyebrow">FITTED TO YOUR LINE</div>
        <h2 className="h2" style={{ marginBottom: 20 }}>We customize. <span className="ac">You don't re-engineer.</span></h2>
        <p className="lede" style={{ fontSize: 19, maxWidth: 600, marginBottom: 40 }}>Each install is configured to your parts, your PLC and your reporting, and keeps up as your products change.</p>
        <div className="grid g4 steps">
          {STEPS.map(s => (
            <div className="step" key={s.t}>
              <div className="rail"><i /><b /></div>
              <div className="w">{s.when}</div>
              <div className="t">{s.t}</div>
              <div className="d">{s.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
